import { useEffect, useRef, useState } from 'react';

interface ProgressiveListProps<T> {
  items: T[];
  initial?: number;
  step?: number;
  renderItem: (item: T, index: number) => JSX.Element;
}

export default function ProgressiveList<T>({ items, initial = 50, step = 50, renderItem }: ProgressiveListProps<T>) {
  const [visible, setVisible] = useState<number>(Math.min(initial, items.length));
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setVisible(Math.min(initial, items.length));
  }, [items, initial]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setVisible((v) => Math.min(items.length, v + step));
        }
      }
    }, { rootMargin: '200px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [items.length, step]);

  const list = items.slice(0, visible);

  return (
    <div>
      {list.map((item, idx) => renderItem(item, idx))}
      <div ref={sentinelRef} />
      {visible < items.length && (
        <div style={{ textAlign: 'center', margin: '8px 0' }}>
          <button className="btn-secondary" onClick={() => setVisible(Math.min(items.length, visible + step))}>Show more</button>
        </div>
      )}
    </div>
  );
}

